/**
 * Editor Page — unified flow composition shell.
 *
 * Layout: TabBar → PipelineStepList (always visible) → DebugPanel → EditorPanel.
 * Drawer panels (tools, settings, pipeline) via DrawerLayout (FR-NAV-1).
 * Pipeline strip visible for 1+ steps (FR-NAV-2).
 * New tabs show tool selector overlay (FR-NAV-3).
 * Debug panel between pipeline strip and editor (FR-NAV-5).
 *
 * Tab ↔ AppStore sync: each tab is a state container. Switching tabs
 * saves the current appStore state to the old tab, then loads the new
 * tab's state into appStore.
 */

import React, { useCallback,useState } from 'react';

import { PipelineStepSettings } from '@/components/app/PipelineStepSettings';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { DebugPanel } from '@/components/debug/DebugPanel';
import {
  DeletePipelinePopup,
  EditPipelinePopup,
  SavePipelineChoicePopup,
  SavePipelinePopup,
} from '@/components/dialogs/PipelineDialogs';
import { EditorPanel } from '@/components/editor/EditorPanel';
import { TabBar } from '@/components/editor/TabBar';
import { DrawerLayout } from '@/components/layout/DrawerLayout';
import { PipelineStepList } from '@/components/pipeline/PipelineStepList';
import { PipelineVariablesPopup } from '@/components/pipeline/PipelineVariablesPopup';
import { TabToolbar } from '@/components/sidebar/TabToolbar';
import { useEditorSelection } from '@/hooks/useEditorSelection';
import { usePipelineManager } from '@/hooks/usePipelineManager';
import { useToolSidebar } from '@/hooks/useToolSidebar';
import { hydrateActiveTabStateToApp, loadTabStateToApp, saveAppStateToTab } from '@/lib/tabStateSync';
import { useEditorStore } from '@/store/useEditorStore';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useTabStore } from '@/store/useTabStore';

// ─── Component ─────────────────────────────────────────────────────────────

export const EditorPage: React.FC = () => {
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);

  const activeTabId = useTabStore((s) => s.activeTabId);
  const tabs = useTabStore((s) => s.tabs);
  const setTabStatus = useTabStore((s) => s.setTabStatus);
  const closeTab = useTabStore((s) => s.closeTab);

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const isSelecting = activeTab?.status === 'selecting';

  const {
    selectedToolId,
    selectedPresetId,
    selectedStepId,
    pipelineSettingsOpen,
    handleOpenPipelineSettings,
    closePipelineSettings,
    resetStepSelection,
    clearToolSelection,
    handleAddTool,
    handleAddPresetToPipeline,
    handleSelectStep,
    handleSelectPreset,
  } = useEditorSelection();

  const {
    sortedPipelines,
    loadedPipelineOutputType,
    openPipeline,
    openEditPipeline,
    openDeletePipeline,
    openSavePipeline,
    saveDialogProps,
    saveChoiceDialogProps,
    editDialogProps,
    deleteDialogProps,
  } = usePipelineManager({
    resetStepSelection,
    clearToolSelection,
  });

  const {
    filteredFavoriteItems,
    recommendationMap,
    isFavorite,
    toggleFavorite,
  } = useToolSidebar({ loadedPipelineOutputType });

  // Open tools drawer when tab enters 'selecting' status
  React.useEffect(() => {
    if (isSelecting) {
      useLayoutStore.getState().openDrawer('tools');
    }
  }, [isSelecting]);

  // Close 'selecting' tab if tools drawer is dismissed without picking a tool
  React.useEffect(() => {
    const unsub = useLayoutStore.subscribe((state, prevState) => {
      if (prevState.activeDrawer === 'tools' && state.activeDrawer !== 'tools') {
        const tab = useTabStore.getState().tabs.find((t) => t.id === activeTabId);
        if (tab?.status === 'selecting') {
          closeTab(activeTabId);
        }
      }
    });
    return unsub;
  }, [activeTabId, closeTab]);

  // ── Drawer handlers ─────────────────────────────────────────────────────
  const handleAddStep = useCallback(() => {
    useLayoutStore.getState().openDrawerForPipeline();
  }, []);

  const handleDrawerAddTool = useCallback(
    (toolId: string) => {
      handleAddTool(toolId);
      // If this is a new tab in 'selecting' state, activate it
      const currentTab = useTabStore.getState().tabs.find((t) => t.id === activeTabId);
      if (currentTab?.status === 'selecting') {
        setTabStatus(activeTabId, 'active');
      }
      // Sync tab name from pipeline auto-name
      const pipelineName = useEditorStore.getState().activePipelineName;
      if (pipelineName) {
        useTabStore.getState().renameTab(activeTabId, pipelineName);
      }
      useLayoutStore.getState().closeDrawer();
    },
    [handleAddTool, activeTabId, setTabStatus]
  );

  const handleDrawerAddPreset = useCallback(
    (presetId: string, toolId: string, config: Record<string, unknown>, name: string) => {
      const currentTab = useTabStore.getState().tabs.find((t) => t.id === activeTabId);
      if (currentTab?.status === 'selecting') {
        setTabStatus(activeTabId, 'active');
      }
      handleAddPresetToPipeline(presetId, toolId, config, name);
      useLayoutStore.getState().closeDrawer();
    },
    [handleAddPresetToPipeline, activeTabId, setTabStatus]
  );

  // Wrench icon on step chip: select step + open settings drawer
  const handleOpenStepSettings = useCallback(
    (stepId: string) => {
      handleSelectStep(stepId);
      useLayoutStore.getState().openDrawer('settings');
    },
    [handleSelectStep]
  );

  // ── Tab ↔ AppStore sync ─────────────────────────────────────────────────
  const prevTabIdRef = React.useRef(activeTabId);
  const tabSwitchInProgressRef = React.useRef(false);

  React.useEffect(() => {
    const prevId = prevTabIdRef.current;
    if (prevId !== activeTabId) {
      tabSwitchInProgressRef.current = true;
      saveAppStateToTab(prevId);
      loadTabStateToApp(activeTabId);
      resetStepSelection();
      clearToolSelection();
      tabSwitchInProgressRef.current = false;
    }
    prevTabIdRef.current = activeTabId;
  }, [activeTabId, resetStepSelection, clearToolSelection]);

  // ── Initial mount: hydrate AppStore from persisted tab state ──────────
  React.useEffect(() => {
    const persist = useTabStore.persist;
    if (persist.hasHydrated()) {
      hydrateActiveTabStateToApp();
      return;
    }
    const unsubscribe = persist.onFinishHydration(() => {
      hydrateActiveTabStateToApp();
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // ── Flush active tab before unload / HMR ────────────────────────────────
  React.useEffect(() => {
    const flush = () => saveAppStateToTab(useTabStore.getState().activeTabId);
    const onVisibilityChange = () => { if (document.hidden) flush(); };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (import.meta.hot) {
      import.meta.hot.dispose(flush);
    }
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // ── Periodic autosave (every 5s) — persists active tab to tab store ─────
  React.useEffect(() => {
    const AUTO_SAVE_INTERVAL_MS = 2_000;
    const interval = setInterval(() => {
      if (tabSwitchInProgressRef.current) return;
      saveAppStateToTab(useTabStore.getState().activeTabId);
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // ── Eager save: flush to tab store whenever pipeline changes ──────────
  const pipelineRef = React.useRef(useEditorStore.getState().pipeline);
  React.useEffect(() => {
    return useEditorStore.subscribe((state) => {
      if (state.pipeline !== pipelineRef.current) {
        pipelineRef.current = state.pipeline;
        setTimeout(() => {
          if (tabSwitchInProgressRef.current) return;
          saveAppStateToTab(useTabStore.getState().activeTabId);
        }, 0);
      }
    });
  }, []);

  // ── Tab close: save before closing ──────────────────────────────────────
  const handleCloseTab = React.useCallback(
    (tabId: string) => {
      const tabStore = useTabStore.getState();
      // If closing the active tab, save current state first
      if (tabId === tabStore.activeTabId) {
        saveAppStateToTab(tabId);
      }
      tabStore.closeTab(tabId);
      // After close, the store picks a new activeTabId.
      // Load that into appStore.
      const newActiveId = useTabStore.getState().activeTabId;
      loadTabStateToApp(newActiveId);
      prevTabIdRef.current = newActiveId;
      resetStepSelection();
    },
    [resetStepSelection]
  );

  // ── Cmd+K opens tools drawer ─────────────────────────────────────────────
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey && e.key === 'k') {
        e.preventDefault();
        useLayoutStore.getState().openDrawer('tools');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <DrawerLayout
      toolsContent={
        <TabToolbar
          onSelectTool={handleDrawerAddTool}
          onAddToolToPipeline={handleDrawerAddTool}
          filteredFavoriteItems={filteredFavoriteItems}
          selectedToolId={selectedToolId}
          selectedPresetId={selectedPresetId}
          recommendationMap={recommendationMap}
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          onSelectPreset={handleSelectPreset}
          onAddPresetToPipeline={handleDrawerAddPreset}
          sortedPipelines={sortedPipelines}
          onOpenPipeline={(pipeline) => {
            const currentTab = useTabStore.getState().tabs.find((t) => t.id === activeTabId);
            if (currentTab?.status === 'selecting') {
              setTabStatus(activeTabId, 'active');
            }
            openPipeline(pipeline);
            // Sync tab name to pipeline name
            if (pipeline.name) {
              useTabStore.getState().renameTab(activeTabId, pipeline.name);
            }
            useLayoutStore.getState().closeDrawer();
          }}
          onEditPipeline={openEditPipeline}
          onDeletePipeline={openDeletePipeline}
        />
      }
      settingsContent={
        <PipelineStepSettings
          stepId={selectedStepId}
          pipelineSettingsOpen={pipelineSettingsOpen}
        />
      }
    >
      <div className="h-full flex flex-col overflow-hidden">
        <TabBar onCloseTab={handleCloseTab} />

        <PipelineStepList
          selectedStepId={selectedStepId}
          onSelectStep={handleSelectStep}
          onOpenSettings={handleOpenStepSettings}
          onOpenPipelineSettings={handleOpenPipelineSettings}
          onSavePipeline={openSavePipeline}
          onAddStep={handleAddStep}
          debugActive={debugPanelOpen}
          onToggleDebug={() => setDebugPanelOpen((v) => !v)}
        />

        <DebugPanel
          open={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
        />

        <div className="flex-1 min-h-0">
          <EditorPanel />
        </div>

        <PipelineVariablesPopup
          open={pipelineSettingsOpen}
          onClose={closePipelineSettings}
        />

        <ErrorBoundary>
          <SavePipelineChoicePopup {...saveChoiceDialogProps} />
          <SavePipelinePopup {...saveDialogProps} />
          <EditPipelinePopup {...editDialogProps} />
          <DeletePipelinePopup {...deleteDialogProps} />
        </ErrorBoundary>
      </div>
    </DrawerLayout>
  );
};
